import APITemplate from '@api/APITemplate'
import { diffTimeMinutes } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { transcricoes as endpoint } from '@modules/crm/constants/URL'

// API do domínio de Projetos de transcrição (CRM > Transcrições > Projetos).
// Segue o modelo APITemplate/apiCrmFunilSteps: cache, dedup e dispatch pro store
// vivem AQUI (não na action). A lista de projetos é estável por escritório, então
// é cacheada na store (crm/transcriptProjects) com TTL de MINUTES_TIMEOUT_REQUESTS.
// Os componentes chamam carregar()/salvar()/remover() direto e leem pelos getters;
// não há action intermediária (mesmo padrão do apiCrmCalendar).
class APICrmTranscricaoProjetos extends APITemplate {
  constructor() {
    super()

    this.MODULE = 'crm'
    this.reqCarregar = null

    // STORE KEYS (getters)
    this.KEY_DATA = `${this.MODULE}/transcriptProjects`
    this.KEY_LOADING = `${this.MODULE}/transcriptProjectsLoading`
    this.KEY_LAST_UPDATE = `${this.MODULE}/transcriptProjectsLastUpdate`

    // STORE ACTIONS (setters)
    this.ACTION_SET_DATA = `${this.MODULE}/setTranscriptProjects`
    this.ACTION_SET_LOADING = `${this.MODULE}/setTranscriptProjectsLoading`
  }

  // ---------- HELPERS ----------
  // GET /projects → value como array direto ([Project]); tolera envelope
  // { projects: [...] }. Normalização única (antes triplicada em modal/action/mutation).
  normalizeProjects(value) {
    const data = value || {}
    if (Array.isArray(data)) return data
    return Array.isArray(data.projects) ? data.projects : []
  }

  shouldUseCache(forceUpdate) {
    if (forceUpdate) return false

    return !diffTimeMinutes(
      store.getters[this.KEY_LAST_UPDATE],
      MINUTES_TIMEOUT_REQUESTS
    )
  }

  setDataFromResponse(r) {
    if (r && r.status === 200) {
      store.dispatch(this.ACTION_SET_DATA, this.normalizeProjects(r.value))
    }
  }

  async getEarlyResponse(forceUpdate) {
    if (this.reqCarregar) {
      const r = await this.reqCarregar
      return (
        r || this.buildErrorResponse({ message: 'Erro ao carregar projetos' })
      )
    }

    if (this.shouldUseCache(forceUpdate)) {
      return this.buildSuccessResponse({
        message: 'Projetos carregados da memória',
        value: JSON.parse(JSON.stringify(store.getters[this.KEY_DATA] || [])),
      })
    }

    if (store.getters[this.KEY_LOADING]) {
      return this.buildSuccessResponse({
        message: 'Requisição em andamento, projetos carregados da memória',
        value: JSON.parse(JSON.stringify(store.getters[this.KEY_DATA] || [])),
      })
    }

    return null
  }

  // ---------- REQUESTS ----------
  // Lista de projetos do escritório (seletor de vínculo + aba de configuração).
  // Cada item pode trazer is_global:true (modelo global do escritório-template,
  // read-only). forceUpdate revalida após criar/editar/remover.
  async carregar({ forceUpdate = false } = {}) {
    const earlyResponse = await this.getEarlyResponse(forceUpdate)
    if (earlyResponse) return earlyResponse

    store.dispatch(this.ACTION_SET_LOADING, true)

    this.reqCarregar = con.get(endpoint.projectsList)
    const r = await this.reqCarregar

    this.setDataFromResponse(r)

    store.dispatch(this.ACTION_SET_LOADING, false)
    this.reqCarregar = null

    return r || this.buildErrorResponse({ message: 'Erro ao carregar projetos' })
  }

  // Cria (sem id → POST) ou edita (com id → PUT) um projeto e revalida a lista.
  // type ∈ {interno, comercial-prospeccao, comercial-relacionamento,
  // externo-estrategico}. HTTP 200 sempre; status real no body (400 type/name
  // inválido, 404 modelo global de outro escritório). Owner-scoped no backend.
  async salvar({ id = null, name, type, description } = {}) {
    const body = { name, type, description }
    const r = id
      ? await con.put(endpoint.projectDetail(id), body)
      : await con.post(endpoint.projectsList, body)

    if (r && r.status === 200) {
      this.carregar({ forceUpdate: true })
    }

    return r || this.buildErrorResponse({ message: 'Erro ao salvar projeto.' })
  }

  // Remove um projeto (owner-scoped: modelo global de outro escritório → 404) e
  // revalida a lista no store.
  async remover({ id } = {}) {
    if (!id) {
      return this.buildErrorResponse({
        status: 404,
        message: 'Projeto não encontrado.',
      })
    }

    const r = await con.delet(endpoint.projectDetail(id))

    if (r && r.status === 200) {
      this.carregar({ forceUpdate: true })
    }

    return r || this.buildErrorResponse({ message: 'Erro ao remover projeto.' })
  }

  // Dashboard gestor-only (spec-005): agregação de scores do escritório do
  // contexto. Read-only e sem cache (números mudam a cada avaliação). value =
  // { projects:[{id_project, name_project, avg_score, n_meetings}],
  // professionals:[{id_profissional, nome, avg_score, n_meetings}],
  // summary:{total_projects, total_professionals, avg_score_overall,
  // total_meetings} }. Não-gestor → status 403 no body.
  async dashboardProjetos() {
    return (
      (await con.get(endpoint.dashboard)) ||
      this.buildErrorResponse({ message: 'Erro inesperado' })
    )
  }
}

const apiCrmTranscricaoProjetos = new APICrmTranscricaoProjetos()
export { apiCrmTranscricaoProjetos }
